import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import Header from '../Header';
import AdminNav from './AdminNav';
import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../services/online/apiClient';

const PAGE_SIZE = 20;

function statusColor(status) {
    if (status === 'BLOCKED') return 'error';
    if (status === 'DELETED') return 'default';
    return 'success';
}

export default function AdminUsersScreen() {
    const { authFetch, user: me } = useAuth();

    const [q, setQ] = useState('');
    const [role, setRole] = useState('');
    const [status, setStatus] = useState('');
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [detailUser, setDetailUser] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const buildQuery = useCallback(
        (offset) => {
            const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
            if (q.trim()) params.set('q', q.trim());
            if (role) params.set('role', role);
            if (status) params.set('status', status);
            return params.toString();
        },
        [q, role, status]
    );

    const load = useCallback(
        async (offset = 0) => {
            setError(null);
            setIsLoading(true);
            try {
                const result = await authFetch(`/admin/users?${buildQuery(offset)}`);
                if (offset === 0) setItems(result.items);
                else setItems((prev) => [...prev, ...result.items]);
                setTotal(result.total);
            } catch (err) {
                setError(err instanceof ApiError ? err.message : 'No se pudo cargar la lista de usuarios.');
            } finally {
                setIsLoading(false);
            }
        },
        [authFetch, buildQuery]
    );

    useEffect(() => {
        load(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, role, status]);

    const handleToggleBlock = async (u) => {
        setBusyId(u.id);
        setError(null);
        try {
            const action = u.status === 'BLOCKED' ? 'unblock' : 'block';
            const updated = await authFetch(`/admin/users/${u.id}/${action}`, { method: 'POST' });
            setItems((prev) => prev.map((it) => (it.id === u.id ? { ...it, status: updated.status } : it)));
        } catch (err) {
            setError(err instanceof ApiError ? err.message : 'La acción ha fallado.');
        } finally {
            setBusyId(null);
        }
    };

    const handleToggleRole = async (u) => {
        const nextRole = u.role === 'ADMIN' ? 'USER' : 'ADMIN';
        if (u.id === me?.id && nextRole === 'USER') {
            if (!window.confirm('Vas a quitarte el rol de administrador a ti mismo. ¿Continuar?')) return;
        } else if (!window.confirm(`¿${nextRole === 'ADMIN' ? 'Promocionar' : 'Quitar el rol de admin a'} "${u.name || u.email}"?`)) {
            return;
        }
        setBusyId(u.id);
        setError(null);
        try {
            const updated = await authFetch(`/admin/users/${u.id}/role`, { method: 'PATCH', body: { role: nextRole } });
            setItems((prev) => prev.map((it) => (it.id === u.id ? { ...it, role: updated.role } : it)));
        } catch (err) {
            setError(err instanceof ApiError ? err.message : 'La acción ha fallado.');
        } finally {
            setBusyId(null);
        }
    };

    const openDetail = async (u) => {
        setDetailUser(u);
        setDetail(null);
        setDetailLoading(true);
        try {
            const result = await authFetch(`/admin/users/${u.id}/rooms`);
            setDetail(result);
        } catch (err) {
            setDetail({ error: err instanceof ApiError ? err.message : 'No se pudo cargar el detalle.' });
        } finally {
            setDetailLoading(false);
        }
    };

    return (
        <>
            <Header title="Usuarios" showSettings={false} />
            <AdminNav />
            <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                    <TextField label="Buscar por nombre o email" value={q} onChange={(e) => setQ(e.target.value)} size="small" fullWidth />
                    <TextField select label="Rol" value={role} onChange={(e) => setRole(e.target.value)} size="small" sx={{ minWidth: 140 }}>
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="USER">USER</MenuItem>
                        <MenuItem value="ADMIN">ADMIN</MenuItem>
                    </TextField>
                    <TextField select label="Estado" value={status} onChange={(e) => setStatus(e.target.value)} size="small" sx={{ minWidth: 160 }}>
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                        <MenuItem value="BLOCKED">BLOCKED</MenuItem>
                        <MenuItem value="DELETED">DELETED</MenuItem>
                    </TextField>
                </Stack>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <TableContainer sx={{ bgcolor: 'background.paper' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Nombre</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Rol</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell>Alta</TableCell>
                                <TableCell align="right">Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((u) => (
                                <TableRow key={u.id} hover>
                                    <TableCell onClick={() => openDetail(u)} sx={{ cursor: 'pointer' }}>{u.name || '—'}</TableCell>
                                    <TableCell onClick={() => openDetail(u)} sx={{ cursor: 'pointer' }}>{u.email}</TableCell>
                                    <TableCell>
                                        <Chip size="small" label={u.role} color={u.role === 'ADMIN' ? 'primary' : 'default'} />
                                    </TableCell>
                                    <TableCell>
                                        <Chip size="small" label={u.status} color={statusColor(u.status)} />
                                    </TableCell>
                                    <TableCell>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}</TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                            <Button
                                                size="small"
                                                disabled={busyId === u.id || u.status === 'DELETED'}
                                                onClick={() => handleToggleBlock(u)}
                                            >
                                                {u.status === 'BLOCKED' ? 'Desbloquear' : 'Bloquear'}
                                            </Button>
                                            <Button
                                                size="small"
                                                color="secondary"
                                                disabled={busyId === u.id || u.status === 'DELETED'}
                                                onClick={() => handleToggleRole(u)}
                                            >
                                                {u.role === 'ADMIN' ? 'Quitar admin' : 'Hacer admin'}
                                            </Button>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!isLoading && items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>Sin resultados.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!isLoading && items.length < total && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button onClick={() => load(items.length)}>Cargar más ({items.length}/{total})</Button>
                    </Box>
                )}
            </Container>

            <Dialog open={Boolean(detailUser)} onClose={() => setDetailUser(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{detailUser?.name || detailUser?.email}</DialogTitle>
                <DialogContent dividers>
                    {detailLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {detail?.error && <Alert severity="error">{detail.error}</Alert>}
                    {detail && !detail.error && (
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="subtitle2">Sala activa</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {detail.activeRoom ? `${detail.activeRoom.name} · ${detail.activeRoom.status} · código ${detail.activeRoom.code}` : 'Ninguna'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2">Historial ({detail.historyTotal})</Typography>
                                {detail.history.length === 0 && <Typography variant="body2" color="text.secondary">Sin partidas jugadas.</Typography>}
                                {detail.history.map((h) => (
                                    <Typography key={h.id} variant="body2" color="text.secondary">
                                        {new Date(h.finishedAt).toLocaleString()} · {h.finalScoreTeam1} - {h.finalScoreTeam2}
                                    </Typography>
                                ))}
                            </Box>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailUser(null)}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
